/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    getPayloadObject,
    getTelemetryAttributes,
    IPayLoad,
    ITelemetryContent,
    KeyCodes,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { DeliveryOption, ChannelConfiguration } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { StorePickUpOptionList } from './store-pickup-option-list';
import { IMapClient, ISuggestion } from '../interfaces';
import { AzureMapsClient, BingMapsClient, NoopMapClient } from './store-selector-search-form-map-clients';

export interface IStoreSelectorSearchFormProps {
    resources: {
        searchInputAriaLabel: string;
        searchButtonAriaLabel: string;
        searchPlaceholderText: string;
        seeAllStoresText: string;
        viewListText: string;
        viewMapText: string;
        pickupFilterByHeading?: string;
        pickupFilterMenuHeading?: string;
    };

    id: string;
    value: string;
    isMapDisabled?: boolean;
    showAllStores?: boolean;
    displayList?: boolean;
    locatorView?: boolean;
    productPickUpDeliveryOptions?: DeliveryOption[];
    hasEnabledPickupFilterToShowStore?: boolean;
    filteredPickupMode?: string;
    useAzureMaps?: boolean;
    autoSuggestionEnabled?: boolean;
    autoSuggestOptions?: Microsoft.Maps.IAutosuggestOptions;
    telemetry?: Msdyn365.ITelemetry;
    channel?: ChannelConfiguration;
    locale?: string;
    isMapAPILoaded?: boolean;

    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    onShowAllStores(): void;
    onToggleListMapViewState(): void;
    performSearch(searchTerm: string, latitude?: number, longitude?: number): Promise<void>;
    searchTermChanged(searchTerm: string): Promise<void>;
    filterPickupModeSelected(pickupMode: string): void;
    onSuggestionsSelected(suggestion: ISuggestion): void;
    sortByLocation?: boolean;
    sortLocations(): void;
    allStore: number;
    loading: boolean;
    isDialogOpen?: boolean;
}
interface IStoreSelectorSearchFormState {
    value: string;
    error: string;
    suggestions: ISuggestion[];
    activeSuggestionId: string;
    isSearchResultOpened: boolean;
}

/**
 * Simple search form consisting of search text and a search button.
 */
export class StoreSelectorSearchForm extends React.PureComponent<IStoreSelectorSearchFormProps, IStoreSelectorSearchFormState> {
    public mapClient: IMapClient;
    private readonly searchBoxRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly storeSearchAttributes: Msdyn365.IDictionary<string> | undefined;

    private readonly showAllStoreAttributes: Msdyn365.IDictionary<string> | undefined;

    private readonly toggleMapViewAttributes: Msdyn365.IDictionary<string> | undefined;
    private previousValue: string = '';

    constructor(props: IStoreSelectorSearchFormProps) {
        super(props);
        this.state = {
            suggestions: [],
            activeSuggestionId: '',
            value: props.value || '',
            isSearchResultOpened: false,
            error: ''
        };
        this._handleKeyPressPrev = this._handleKeyPressPrev.bind(this);
        const payLoad: IPayLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.SearchStore);
        this.storeSearchAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
        payLoad.contentAction.etext = TelemetryConstant.ShowAllStore;
        this.showAllStoreAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
        payLoad.contentAction.etext = TelemetryConstant.ToggleMapView;
        this.toggleMapViewAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
        const telemetry = props.telemetry || ({} as Msdyn365.ITelemetry);
        if (props.autoSuggestionEnabled) {
            this.mapClient = props.useAzureMaps
                ? new AzureMapsClient(props.channel, telemetry, props.locale)
                : new BingMapsClient(props.channel, telemetry, props.autoSuggestOptions);
        } else {
            this.mapClient = new NoopMapClient();
        }
    }

    public componentDidMount(): void {
        this.mapClient.initialize(this.props.isMapAPILoaded || false);
    }

    public componentDidUpdate(prevProps: IStoreSelectorSearchFormProps): void {
        if (this.props.isMapAPILoaded && !prevProps.isMapAPILoaded) {
            this.mapClient.initialize(true);
        }

        if (!prevProps.isDialogOpen && this.props.isDialogOpen) {
            this.setState({ error: '' });
        }
    }

    public onShowAllStoresHandler = () => {
        this.props.onShowAllStores();
        this.setState({ error: '' });
    };

    public render(): JSX.Element {
        const {
            resources: {
                searchButtonAriaLabel,
                seeAllStoresText,
                viewListText,
                viewMapText,
                pickupFilterByHeading,
                pickupFilterMenuHeading
            },
            id,
            value,
            channel,
            showAllStores,
            displayList,
            locatorView,
            onToggleListMapViewState,
            productPickUpDeliveryOptions,
            hasEnabledPickupFilterToShowStore,
            loading
        } = this.props;
        const isMapDisabled =
            !(channel?.BingMapsApiKey && channel.BingMapsEnabled) || !(channel?.AzureMapsApiKey && channel.AzureMapsEnabled);

        const { activeSuggestionId /*, suggestions */ } = this.state;
        const toggleButtonText = displayList ? viewMapText : viewListText;
        // const suggestionListView = suggestions.map((suggestion: ISuggestion, index: number) => (
        //     <li
        //         key={index}
        //         id={`result-as-${index}`}
        //         role='listitem'
        //         aria-selected={this.state.activeSuggestionId === `result-as-${index}`}
        //         aria-label={`${suggestion.formattedSuggestion}, item ${index + 1}`}
        //         onClick={() => this.onSuggestionSelected(suggestion)}
        //         tabIndex={0}
        //         onKeyDown={(event: React.KeyboardEvent) => this.handleSuggestionListKeyDown(event, index, suggestions.length)}
        //     >
        //         <div
        //             className={`suggestLink ${this.state.activeSuggestionId === `result-as-${index}` ? 'selected' : ''}`}
        //             data-tag='as_suggestion'
        //         >
        //             <div className='as_suggestion_root_inside' data-tag='as_suggestion_root_inside'>
        //                 <div className='as_img maps_address' data-tag='as_img' style={{ height: '37px' }}></div>
        //                 <div className='as_lines_root' data-tag='as_lines_root'>
        //                     <p className='as_lines_suggestion_title' data-tag='as_suggestion_line'>
        //                         {suggestion.title}
        //                     </p>
        //                     <p className='as_lines_suggestion_sub_title' data-tag='as_suggestion_line'>
        //                         {suggestion.subtitle}
        //                     </p>
        //                 </div>
        //             </div>
        //         </div>
        //     </li>
        // ));

        return (
            <div className='ms-store-select__search'>
                <form
                    className='ms-store-select__search-form'
                    aria-label={searchButtonAriaLabel}
                    name='storeSelectorSearchForm'
                    role='form'
                    autoComplete='off'
                    onSubmit={this.onSubmit}
                    id={`ms-store-select__search-box-container_${id}`}
                >
                    <input
                        type='search'
                        aria-label={this.props.resources.searchInputAriaLabel}
                        className='msc-form-control ms-store-select__search-input'
                        placeholder='Enter your postcode'
                        value={value}
                        onChange={this.searchTextChanged}
                        id={`ms-store-select__search-box_${id}`}
                        ref={this.searchBoxRef}
                        role='combobox'
                        aria-expanded={this.state.isSearchResultOpened}
                        aria-controls={`as_containerSearch_${id}`}
                        aria-activedescendant={activeSuggestionId}
                        onKeyUp={this._handleInputKeyPress}
                        autoCorrect='off'
                        autoComplete='street-address'
                        autoCapitalize='off'
                        aria-owns={`as_containerSearch_${id}`}
                        aria-autocomplete='list'
                    />
                    {/* <div className='MicrosoftMap' style={{ position: 'static' }}>
                        <div
                            className='as_container_search as_container'
                            id={`as_containerSearch_${id}`}
                            data-tag='as_container'
                            style={{ visibility: this.state.isSearchResultOpened ? 'visible' : 'hidden' }}
                        >
                            <div className='b_cards asOuterContainer'>
                                <ul role='list'>{suggestionListView}</ul>
                            </div>

                            <div className='clear'></div>
                        </div>
                    </div> */}
                    <button
                        className={`ms-store-select__search-button${isMapDisabled ? ' search-btn-disabled' : ''}`}
                        aria-label={searchButtonAriaLabel}
                        color='primary'
                        {...this.storeSearchAttributes}
                    />
                </form>
                {this.state.error && (
                    <div className='mt-3' style={{ color: '#fa141a' }}>
                        {this.state.error}
                    </div>
                )}
                {showAllStores && (
                    <div className='row ms-store-select__search-see-all-stores-row'>
                        <div
                            className='ms-store-select__search-see-all-stores'
                            tabIndex={0}
                            role='link'
                            onKeyUp={this._handleKeyPressPrev}
                            onClick={this.onShowAllStoresHandler}
                            {...this.showAllStoreAttributes}
                            style={
                                loading
                                    ? {
                                          pointerEvents: 'none',
                                          opacity: 0.5
                                      }
                                    : {}
                            }
                        >
                            {seeAllStoresText}
                        </div>
                        {/* <div onClick={sortLocations} className='ms-store-select__search-see-all-stores'>
                            {sortByLocation ? 'Sort A-Z' : 'Sort By Distance'}
                        </div> */}
                    </div>
                )}
                {locatorView && (
                    <button className='ms-store-select__toggle-view' onClick={onToggleListMapViewState} {...this.toggleMapViewAttributes}>
                        {toggleButtonText}
                    </button>
                )}
                {this.renderPickupModesList(
                    this.props,
                    toggleButtonText,
                    productPickUpDeliveryOptions,
                    pickupFilterMenuHeading,
                    locatorView,
                    pickupFilterByHeading,
                    hasEnabledPickupFilterToShowStore
                )}
            </div>
        );
    }

    public readonly searchTextChanged = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const inputValue: string = event.target.value;

        this.setState({ value: inputValue });
        if (inputValue && inputValue !== '') {
            this.mapClient.getSuggestions(inputValue).then(suggestions => {
                this.setState({ suggestions, isSearchResultOpened: true });
            });
        } else if (inputValue === '') {
            this.setState({ isSearchResultOpened: false, suggestions: [] });
        }
        await this.props.searchTermChanged(inputValue);
    };

    /**
     * Method called on search suggestion selected.
     * @param suggestion - Selected suggestion.
     */
    public readonly onSuggestionSelected = (suggestion: ISuggestion): void => {
        this.searchBoxRef.current?.blur();
        this.props.onSuggestionsSelected({
            formattedSuggestion: suggestion.formattedSuggestion,
            title: suggestion.title,
            subtitle: suggestion.subtitle,
            latitude: suggestion.latitude,
            longitude: suggestion.longitude
        });
        this.setState({ isSearchResultOpened: false, value: suggestion.formattedSuggestion });
    };

    /**
     * Method called on search submit.
     * @param event - Input value.
     */
    public readonly onSubmit = async (event: React.SyntheticEvent): Promise<void> => {
        event.preventDefault(); // Prevents form submission
        this.searchBoxRef.current?.blur();
        const suggestions = this.props.useAzureMaps ? await this.mapClient.getSuggestions(this.state.value) : [];
        const suggestion = suggestions[0];
        await this.props.performSearch(suggestion?.formattedSuggestion || this.state.value, suggestion?.latitude, suggestion?.longitude);
        this.setState({ isSearchResultOpened: false });
    };

    /**
     * Method called on product pickup option change.
     * @returns - Void.
     */
    public readonly onChangeHandler = () => async (deliveryCode: string): Promise<void> => {
        this.props.filterPickupModeSelected(deliveryCode);
        return Promise.resolve();
    };

    /**
     * Handles the blur event on the suggestion list.
     * If the last suggestion is blurred, it closes the search result.
     * @param currentIndex - The current index of the suggestion.
     * @param length - The total number of suggestions.
     */
    public readonly handleSuggestionListKeyDown = (event: React.KeyboardEvent, currentIndex: number, length: number): void => {
        console.log('Key pressed:', event.key);
        const { suggestions } = this.state;
        if (event.keyCode === KeyCodes.Enter || event.keyCode === KeyCodes.Space) {
            this.onSuggestionSelected(suggestions[currentIndex]);
        }
        //  when user navigates away from serach results, close the search result.
        if (event.keyCode === KeyCodes.Tab && !event.shiftKey && currentIndex === length - 1) {
            this.setState({ isSearchResultOpened: false });
        }
    };

    private readonly _handleInputKeyPress = (event: React.KeyboardEvent): void => {
        // When the user navigates through the up and down arrow on the result returned by the auto suggest and press enter.

        // At that time, two requests were made to fetch the store details due to which incorrect result is shown and causes flickering.

        // In order to avoid that. Checking if the search result is open or not.

        if (event.keyCode === KeyCodes.ArrowUp || event.keyCode === KeyCodes.ArrowDown) {
            const activeDescedantValue = (event.target as HTMLInputElement).getAttribute('aria-activedescendant');

            if (activeDescedantValue) {
                if (this.previousValue === '') {
                    this.previousValue = activeDescedantValue;

                    this.setState({ isSearchResultOpened: true });

                    return;
                }

                if (activeDescedantValue !== this.previousValue) {
                    this.previousValue = activeDescedantValue;

                    this.setState({ isSearchResultOpened: true });
                } else {
                    this.setState({ isSearchResultOpened: false });
                }
            }
        }
    };

    /**
     * Calls method display result.
     * @param { React.KeyboardEvent} event The first number.
     */
    private readonly _handleKeyPressPrev = (event: React.KeyboardEvent): void => {
        if (event.keyCode === KeyCodes.Enter || event.keyCode === KeyCodes.Space) {
            this.props.onShowAllStores();
        }
    };

    /**
     * Method called on product pickup option change.
     * @returns - Void.
     */

    /**
     * Method to render pickup mode list.
     * @param props - Store selector search form props.
     * @param toggleButtonText - Locator view button text.
     * @param productPickUpDeliveryOptions - Product DeliveryOption List.
     * @param pickupFilterMenuHeading - Pickup store button text.
     * @param locatorView - Locator View is on\off.
     * @param pickupFilterByHeading - Pickup list label text.
     * @param hasEnabledPickupFilterToShowStore - Flag to check header filter is enabled.
     * @returns Jsx component.
     */

    private readonly renderPickupModesList = (
        props: IStoreSelectorSearchFormProps,
        toggleButtonText: string,
        productPickUpDeliveryOptions: DeliveryOption[] | undefined,
        pickupFilterMenuHeading: string | undefined,
        locatorView: boolean | undefined,
        pickupFilterByHeading: string | undefined,
        hasEnabledPickupFilterToShowStore: boolean | undefined
    ): JSX.Element | null => {
        if (hasEnabledPickupFilterToShowStore) {
            return (
                <div className='ms-store-select__search-header'>
                    {locatorView && (
                        <button
                            className='ms-store-select__toggle-view'
                            onClick={props.onToggleListMapViewState}
                            {...this.toggleMapViewAttributes}
                        >
                            {toggleButtonText}
                        </button>
                    )}
                    <StorePickUpOptionList
                        productPickupListOptionMenuText={pickupFilterMenuHeading}
                        defaultOptionText={props.filteredPickupMode ? undefined : pickupFilterMenuHeading}
                        productPickupModeList={productPickUpDeliveryOptions}
                        id={this.props.id}
                        onChange={this.onChangeHandler()}
                    />
                    <div
                        className='ms-store-select__search-header-heading'
                        id={`ms-store-select__search-header-pickup-menu-id_${this.props.id}`}
                    >
                        {pickupFilterByHeading}
                    </div>
                </div>
            );
        }
        if (locatorView) {
            return (
                <button className='ms-store-select__toggle-view' onClick={props.onToggleListMapViewState} {...this.toggleMapViewAttributes}>
                    {toggleButtonText}
                </button>
            );
        }
        return null;
    };
}
